import { useCallback } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { EditorView } from '@lyearn/lygo-core';
import { useSnackbar } from '@lyearn/ui';
import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import useGenerativeTask from '@/components/LyearnAI/hooks/useGenerativeTask';
import { GenerativeTaskTypeEnum } from '@/types/schema';

interface UseVideoScriptGenerationProps {
  editorView: EditorView | undefined;
  onSuccess?: (result: { script: string; fileSummary?: string | null }) => void;
  prevVideoScript?: string;
  slideDeckFilePath?: string;
}

export function useVideoScriptGeneration(props: UseVideoScriptGenerationProps) {
  const { editorView, onSuccess, prevVideoScript, slideDeckFilePath } = props;
  const { t } = useTranslation('Course');
  const { showErrorSnackbar } = useSnackbar();
  const handleTaskError = useEvent((message?: string | null) => {
    showErrorSnackbar({
      message: message || t('Course:AiVideoGeneration.FailedToGenerateVideoScript'),
    });
  });
  const onSuccessEvent = useEvent((generativeTask: GenerativeTaskFragment) => {
    const result = (
      generativeTask.result as
        | {
            generateVideoLessonScript?: {
              videoScript?: string | null;
              fileSummary?: string | null;
            };
          }
        | null
        | undefined
    )?.generateVideoLessonScript;
    const videoScript = result?.videoScript || '';
    onSuccess?.({
      script: videoScript,
      fileSummary: result?.fileSummary,
    });
  });
  const { executeCreateGenerativeTask, generativeTask, generativeTaskId, fetching } =
    useGenerativeTask({
      onSuccess: onSuccessEvent,
      autoCleanup: true,
      onTaskError: (error) => {
        handleTaskError(error?.message);
      },
      suppressDefaultErrorHandling: true,
    });

  const getLessonContent = useCallback(() => {
    if (!editorView) return '';
    return editorView.state.doc.textBetween(0, editorView.state.doc.content.size, '\n\n').trim();
  }, [editorView]);

  const generateVideoScript = useCallback(
    (userPrompt?: string) => {
      const lessonContent = getLessonContent() || undefined;
      const normalizedUserInstruction = userPrompt?.trim() || undefined;
      const normalizedFilePath = slideDeckFilePath?.trim() || undefined;

      if (!lessonContent && !normalizedUserInstruction && !normalizedFilePath) {
        return;
      }
      executeCreateGenerativeTask({
        type: GenerativeTaskTypeEnum.GenerateVideoLessonScript,
        input: {
          generateVideoLessonScript: {
            lessonContent,
            userInstruction: normalizedUserInstruction,
            filePath: normalizedFilePath,
          },
        },
      });
    },
    [getLessonContent, executeCreateGenerativeTask, slideDeckFilePath],
  );

  const regenerateVideoScript = useCallback(
    (userPrompt?: string) => {
      const trimmedPreviousScript = prevVideoScript?.trim() || '';

      if (!trimmedPreviousScript) {
        return;
      }

      const lessonContent = getLessonContent() || undefined;
      const normalizedUserInstruction = userPrompt?.trim() || undefined;
      const normalizedFilePath = slideDeckFilePath?.trim() || undefined;
      executeCreateGenerativeTask({
        type: GenerativeTaskTypeEnum.GenerateVideoLessonScript,
        input: {
          generateVideoLessonScript: {
            lessonContent,
            filePath: normalizedFilePath,
            previousGenerativeTaskOutput: trimmedPreviousScript,
            userInstruction: normalizedUserInstruction,
          },
        },
      });
    },
    [getLessonContent, executeCreateGenerativeTask, prevVideoScript, slideDeckFilePath],
  );

  const generatedVideoScript = (
    generativeTask?.result as
      | {
          generateVideoLessonScript?: { videoScript?: string | null };
        }
      | null
      | undefined
  )?.generateVideoLessonScript?.videoScript;

  return {
    generateVideoScript,
    regenerateVideoScript,
    getLessonContent,
    generatedVideoScript,
    isGeneratingVideoScript:
      (fetching || Boolean(generativeTaskId)) && generativeTaskId === generativeTask?.id,
  };
}
